/**
 * アプリ内通知の一覧。
 *
 * `notifications` は運営（CP）が発行する表で、契約者からは**参照と既読更新のみ**。
 * 読む画面が無かったあいだ、`channels=['inapp']` の通知は発行されるだけで
 * 誰も見られず、事実上「書き捨て」だった。容量超過の予告・料金改定の予告・
 * ノードのメンテナンス通知など、**気づかないと実害が出るもの**がここに来る。
 *
 * 既読は「見た」という主張なので、**一覧に出した瞬間に自動で既読にしない**。
 * 自動で既読にすると、critical の通知がバッジから消えたのに誰も読んでいない、
 * という状態が普通に起きる。
 */
import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CursorPagination,
  EmptyState,
  PageHeader,
  Section,
  Switch,
  formatDateTime,
  useCursorPagination,
} from '@mail-sv/ui';
import { BellOff, CheckCheck, Inbox } from 'lucide-react';
import { api, errorMessage } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { ErrorAlert } from '../components/async.js';
import { FormAlert } from '../components/form.js';
import type { AppNotification, CursorPage } from '../lib/api-types.js';

const SEVERITY_LABEL: Record<AppNotification['severity'], string> = {
  info: 'お知らせ',
  warning: 'ご確認ください',
  critical: '至急ご確認ください',
};

const SEVERITY_VARIANT: Record<AppNotification['severity'], 'muted' | 'warning' | 'destructive'> = {
  info: 'muted',
  warning: 'warning',
  critical: 'destructive',
};

export function NotificationsPage() {
  const queryClient = useQueryClient();
  const page = useCursorPagination(20);
  const [unreadOnly, setUnreadOnly] = React.useState(false);

  const list = useQuery({
    queryKey: qk.notifications({ cursor: page.cursor, limit: page.limit, unreadOnly }),
    queryFn: () => {
      const params = new URLSearchParams({ limit: String(page.limit) });
      if (page.cursor) params.set('cursor', page.cursor);
      if (unreadOnly) params.set('unread', 'true');
      return api.get<CursorPage<AppNotification>>(`/notifications?${params.toString()}`);
    },
  });

  const invalidate = React.useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['portal', 'notifications'] as const });
  }, [queryClient]);

  const markRead = useMutation({
    mutationFn: (args: { id: string; read: boolean }) =>
      api.patch(`/notifications/${encodeURIComponent(args.id)}`, { read: args.read }),
    onSuccess: invalidate,
  });

  const readAll = useMutation({
    mutationFn: () => api.post<{ ok: boolean; updated: number }>('/notifications/read-all'),
    onSuccess: invalidate,
  });

  const items = list.data?.items ?? [];

  return (
    <>
      <PageHeader
        title="お知らせ"
        description="ご契約や設定に関する運営からのご連絡です。"
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => readAll.mutate()}
            loading={readAll.isPending}
          >
            <CheckCheck aria-hidden />
            すべて既読にする
          </Button>
        }
      />

      <div className="space-y-5">
        <FormAlert error={markRead.error ?? readAll.error} />

        <div className="flex items-center gap-2">
          <Switch
            id="unread-only"
            checked={unreadOnly}
            onCheckedChange={(v) => {
              setUnreadOnly(v);
              page.reset();
            }}
          />
          <label htmlFor="unread-only" className="text-sm">
            未読だけ表示
          </label>
        </div>

        {list.isError ? <ErrorAlert error={list.error} onRetry={() => void list.refetch()} /> : null}

        <Section title="">
          {items.length === 0 && !list.isPending ? (
            <EmptyState
              icon={BellOff}
              title={unreadOnly ? '未読のお知らせはありません' : 'お知らせはありません'}
              description="ご契約や設定に関するご連絡があると、ここに表示されます。"
            />
          ) : (
            <div className="space-y-3">
              {items.map((n) => (
                <Card key={n.id} className={n.readAt === null ? 'border-primary/40' : undefined}>
                  <CardContent className="space-y-2 p-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant={SEVERITY_VARIANT[n.severity]} size="sm">
                        {SEVERITY_LABEL[n.severity]}
                      </Badge>
                      {n.readAt === null ? (
                        <Badge variant="secondary" size="sm">
                          未読
                        </Badge>
                      ) : null}
                      <span className="text-xs text-muted-foreground">
                        {formatDateTime(n.createdAt)}
                      </span>
                    </div>
                    <p className="text-sm font-medium">{n.title}</p>
                    {/*
                     * 本文は Markdown で届くが、**HTML には変換しない**。
                     * 運営が書いた文字列とはいえ、契約者の画面で任意 HTML を
                     * 解釈する経路を作らない（XSS の面を増やさない）。
                     * 改行だけ活かして素のテキストとして出す。
                     */}
                    <p className="whitespace-pre-wrap text-sm text-muted-foreground">{n.bodyMd}</p>
                    <div className="pt-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          markRead.mutate({ id: n.id, read: n.readAt === null })
                        }
                        loading={markRead.isPending && markRead.variables?.id === n.id}
                      >
                        {n.readAt === null ? '既読にする' : '未読に戻す'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              <CursorPagination
                nextCursor={list.data?.nextCursor ?? null}
                canGoPrev={page.canGoPrev}
                onNext={() => page.goNext(list.data?.nextCursor ?? null)}
                onPrev={page.goPrev}
                limit={page.limit}
                onLimitChange={page.setLimit}
                currentCount={items.length}
                loading={list.isFetching}
              />
            </div>
          )}
        </Section>
      </div>
    </>
  );
}

/**
 * ヘッダのベル。未読件数だけを軽く引く（一覧とは別の口）。
 *
 * `critical` があるときだけ色を変える。すべて同じ色にすると、
 * 「支払いが確認できていません」と「今月の使用量レポート」が同列に見える。
 */
export function NotificationBell({ onOpen }: { onOpen: () => void }) {
  const count = useQuery({
    queryKey: qk.notificationCount(),
    queryFn: () => api.get<{ unread: number; critical: number }>('/notifications/unread-count'),
    // ベルは常時表示なので、頻繁に叩かない。
    staleTime: 60_000,
    refetchInterval: 120_000,
    // 権限が無い / 未ログイン直後の失敗でヘッダを壊さない。
    retry: false,
  });

  const unread = count.data?.unread ?? 0;
  const critical = (count.data?.critical ?? 0) > 0;

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onOpen}
      aria-label={unread > 0 ? `お知らせ ${unread} 件（未読）` : 'お知らせ'}
      title={count.isError ? errorMessage(count.error) : undefined}
    >
      <Inbox aria-hidden />
      <span className="hidden sm:inline">お知らせ</span>
      {unread > 0 ? (
        <Badge variant={critical ? 'destructive' : 'secondary'} size="sm">
          {unread > 99 ? '99+' : unread}
        </Badge>
      ) : null}
    </Button>
  );
}
